import { router } from "@inertiajs/react"
import type { EmblaOptionsType } from "embla-carousel"
import { useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import { useCarousel } from "@/components/ui/carousel"
import { usePlayerKeyboard } from "@/hooks/use-player-keyboard"
import { usePlayerState } from "@/hooks/use-player-state"
import { findBridge, findLiveController } from "@/lib/player/bridges"
import { coordinator } from "@/lib/player/coordinator"
import type { PlayableItem } from "@/lib/player/playable-item"
import type { Playlist, Track } from "@/types"

import type { OutroSlate } from "./paper-types"
import { useFeedFocus } from "./use-feed-focus"

/** Props the play page hands the shell (server maps `?t=` → position). */
export interface FeedShellProps {
  playlist: Playlist
  tracks: Track[]
  /** 1-based position to open on (from `?t=…`). */
  initialPosition?: number | null
  /** Server-resolved onward slate for the outro (Onwards). */
  outroSlate?: OutroSlate | null
  /** Dev+admin `?outro=1`: mount with the outro already open (screenshot aid). */
  forceOutro?: boolean
}

export interface JacketEntry {
  track: Track
  item: PlayableItem
}

/**
 * Per-slide stage. Applies to the SELECTED slide only — every other slide
 * always shows its ambient jacket face.
 *
 *   ambient  — the jacket covers the media (silent; a loop-eligible slide
 *              may run live-muted behind the die-cut)
 *   opening  — the threshold tap fired; the jacket is mid-departure
 *   active   — the media owns the slide; the jacket is docked/parked
 *   upnext   — end-of-media fired; the page-turn choreography is up
 *   closing  — Beat 1 of the re-jacket: the escape's first rung fired; the
 *              stage exits and the record lifts off and reparks HIDDEN
 *              off-screen-right while the empty sleeve crossfades back in as
 *              one composed object (front + back, no record inside yet).
 *              Media mounts stay alive throughout (isOpen counts "closing" as
 *              covered). When the sleeve reports it is composed the shell
 *              hands off to Beat 2 (sleeveComposed).
 *   seating  — Beat 2 (video/photo only): the record — the media-box AND its
 *              vinyl texture as one disc group — slides in from off-right into
 *              the die-cut on springMediaZ and seats. The shell reports
 *              closeSettled when it is home. Article/audio have no media rect,
 *              so they settle straight to ambient at the end of Beat 1.
 */
export type JacketPhase =
  | "ambient"
  | "opening"
  | "active"
  | "upnext"
  | "closing"
  | "seating"

/**
 * Queue derivation shared by every jacket shell — same rules as the
 * immersive feed (unfilled tracks are dropped; `?t=` picks the start).
 * Runs OUTSIDE Carousel.Root: the result feeds Embla's `startIndex`.
 */
export function buildJacketQueue(
  tracks: Track[],
  initialPosition?: number | null,
): { items: JacketEntry[]; startIndex: number } {
  const items = [...tracks]
    .sort((a, b) => a.position - b.position)
    .flatMap((track) => {
      const item = track.playable
      return item ? [{ track, item }] : []
    })
  const found = initialPosition
    ? items.findIndex((entry) => entry.track.position === initialPosition)
    : 0
  return { items, startIndex: found < 0 ? 0 : found }
}

/**
 * Shared Embla configuration. `watchDrag` keeps the feed's vertical gesture
 * out of regions that own their own scroll/touch (article reader, audio
 * well, any embed) — evaluated per pointer-down; no reInit anywhere.
 */
export function jacketEmblaOpts(startIndex: number): EmblaOptionsType {
  return {
    loop: false,
    // Centre the focused plate so the REAL neighbours peek symmetrically —
    // the prior jacket hangs in from the top, the next pokes up from the
    // bottom (the slides are sized to leave that peek; see JACKET_SLIDE_CLASS).
    // containScroll trims the empty run at the ends, so the first plate shows
    // only a bottom peek and the last only a top.
    align: "center",
    containScroll: "keepSnaps",
    startIndex,
    watchDrag: (_api, evt) => {
      const target = evt.target as HTMLElement | null
      return !target?.closest?.(
        "[data-reader-scroll], [data-embed-well], iframe",
      )
    },
  }
}

/**
 * Slide sizing: each plate is a hair short of the viewport (`PEEK_PX` off
 * each end, so `2·PEEK_PX` total) and the carousel is centred, so the REAL
 * neighbour plates bleed a `PEEK_PX` strip in at the top and bottom. That
 * void stays bare Oat — a single low-opacity caret (PeekHint in the shell)
 * marks that the stack turns up/down and lets a pointer tap turn one plate;
 * the caret hides while a plate is OPEN so the reading view stays clean.
 * Everything is already loaded; only the live video stream is gated to the
 * selected plate (use-ambient-loop).
 */
export const JACKET_SLIDE_CLASS =
  "relative w-full shrink-0 grow-0 basis-[calc(100%-80px)]"

/**
 * End-of-feed gesture thresholds.
 */
// For scrolling the mouse wheel or swipe down beyond
// a specified number of pixels to opens the outro.
const END_WHEEL_PX = 8
const END_SWIPE_PX = 48
// One page-turn per wheel gesture then after a turn fires, ignore further wheel
// ticks until the wheel has been idle this long.
const WHEEL_IDLE_MS = 200

/** Does this item's embed have a provider bridge (today: YouTube, Spotify)? */
export function isBridged(item: PlayableItem): boolean {
  return !!item.embed_url && !!findBridge(item.embed_url)
}

/**
 * Can this item's bridge mute audibly in place? Spotify is bridged for
 * play-state (the record's spin) but its embed exposes no volume command, so
 * its mute toggle stays suppressed — never promise a mute we can't deliver.
 */
export function hasMutableAudio(item: PlayableItem): boolean {
  return (
    isBridged(item) &&
    !item.embed_url?.startsWith("https://open.spotify.com/embed/")
  )
}

/**
 * The `muted` prop for a live ContentPlayer mount. Bridged embeds FREEZE it
 * at true — flipping the prop rebuilds the iframe src and reloads the embed,
 * so their audible mute travels via the bridge controller instead
 * (setLiveMuted below). Unbridged embeds follow the coordinator: the reload
 * on toggle is the sanctioned cut.
 */
export function liveMutedProp(item: PlayableItem, isMuted: boolean): boolean {
  return isBridged(item) ? true : isMuted
}

export interface UseJacketFeedOptions {
  playlist: Playlist
  items: JacketEntry[]
  /**
   * Per-item ambient-loop gate. Items where this returns true run a
   * live-muted loop behind the jacket from selection onward: the
   * coordinator slot follows Embla's selection, and the threshold tap
   * unmutes IN PLACE via the bridge — the loop never restarts. Everything
   * else is poster-first: the tap claims the slot. Omit for a fully
   * poster-first shell. MUST be referentially stable (memoized) — it sits
   * in effect deps.
   */
  ambientLive?: (item: PlayableItem) => boolean
  /** Dev+admin `?outro=1`: seed the outro open on the last plate (screenshot aid). */
  forceOutro?: boolean
}

export function useJacketFeed({
  playlist,
  items,
  ambientLive,
  forceOutro,
}: UseJacketFeedOptions) {
  const { api, selectedIndex, scrollPrev, scrollNext } = useCarousel()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { isMuted } = usePlayerState()
  const reduce = useReducedMotion() ?? false
  const [phase, setPhase] = useState<JacketPhase>("ambient")
  // `forceOutro` (dev+admin ?outro=1) seeds it open for a clean first paint.
  const [finished, setFinished] = useState(Boolean(forceOutro))
  const forcedRef = useRef(false)
  // Items where the viewer chose "stay": Up Next stays suppressed for them
  // — a late duration-timer fire must not rip them away — until manual
  // navigation (the Embla select reset) clears the latch.
  const stayedRef = useRef(new Set<string>())
  // Whether the current open should auto-unmute once the media LANDS on the
  // stage (§7: the needle drops when the record lands) — bridged fresh
  // mounts only; they unmute in place, no iframe reload.
  const unmuteOnLandRef = useRef(false)

  // These ref for re-subscribing once a page-turn per wheel gesture.
  const wheelLockedRef = useRef(false)
  const wheelIdleRef = useRef<number | undefined>(undefined)

  const current: JacketEntry | undefined = items[selectedIndex]
  const next: JacketEntry | undefined = items[selectedIndex + 1]
  const currentItem = current?.item
  const currentLoops = !!currentItem && !!ambientLive?.(currentItem)
  // Kinds whose media rides the disc out to the stage — the only kinds that
  // get Beat 2 (the record slides back in). Article/audio settle at Beat 1.
  const currentTravels =
    currentItem?.kind === "video" || currentItem?.kind === "photo"

  useFeedFocus(containerRef, selectedIndex)

  // Reset point: Embla "select" = a new page. Every jacket starts silent
  // and covered. Sanctioned subscription-callback setState (the React
  // Compiler forbids mirroring selectedIndex in an effect body).
  useEffect(() => {
    if (!api) return
    const onSelect = () => {
      setPhase("ambient")
      unmuteOnLandRef.current = false
      stayedRef.current.clear()
      coordinator.setMuted(true)
      // Every page change releases the slot — synchronously, in the Embla
      // event. Loop items are re-claimed by the claim effect on the render
      // that follows (whose cleanup for the departed item sees null and
      // no-ops), so the order is always release → claim; the one-render
      // null window is unobservable on this route.
      coordinator.setActive(null)
      if (api.selectedScrollSnap() !== items.length - 1) setFinished(false)
    }
    api.on("select", onSelect)
    return () => {
      api.off("select", onSelect)
    }
  }, [api, items.length])

  // Dev+admin force-open (?outro=1): land on the LAST plate so the select
  // reset above (which flips `finished` off when `selectedScrollSnap() !==
  // last`) is satisfied and can never clobber the forced state — e.g. on an
  // Embla reInit. Runs exactly once, after `api` is ready.
  useEffect(() => {
    if (!forceOutro || forcedRef.current || !api) return
    forcedRef.current = true
    if (items.length > 0) api.scrollTo(items.length - 1, true)
    setFinished(true)
  }, [api, forceOutro, items.length])

  // Loop items own the coordinator slot from selection onward — the
  // live-muted loop runs behind the jacket (mirrors the immersive feed's
  // activeItem effect, race-guarded release included). Also covers the
  // `?t=` deep link onto a loop slide: no select event fires at mount.
  useEffect(() => {
    if (!currentItem || !ambientLive?.(currentItem)) return
    coordinator.setActive(currentItem.id)
    return () => {
      if (coordinator.getSnapshot().activeId === currentItem.id) {
        coordinator.setActive(null)
      }
    }
  }, [ambientLive, currentItem])

  // Poster-first items hold the slot outside any effect (the tap claims
  // it), so release on unmount (route change) here — a guarded no-op for
  // loop items, whose claim effect already released. `items` is
  // referentially stable for a mounted shell — this runs at unmount in
  // practice.
  useEffect(() => {
    return () => {
      const { activeId } = coordinator.getSnapshot()
      if (activeId && items.some((entry) => entry.item.id === activeId)) {
        coordinator.setActive(null)
      }
    }
  }, [items])

  // End-of-media → Up Next (or the outro on the last plate). Subscribed only
  // while Active: A's ambient loop ending under the leaf is the shell's
  // business (poster swap), never a page turn. All end-of-media arrives here
  // through coordinator.emitEnded — bridge ended, duration timer, LivePhoto's
  // 6s, the article colophon.
  useEffect(() => {
    if (!currentItem || phase !== "active") return
    return coordinator.onEnded(currentItem.id, () => {
      if (stayedRef.current.has(currentItem.id)) return // "stay" means stay
      if (selectedIndex >= items.length - 1) setFinished(true)
      else setPhase("upnext")
    })
  }, [currentItem, phase, selectedIndex, items.length])

  /**
   * Mute for shells: coordinator state (the chrome's truth) plus the live
   * bridge controller in place — bridged live mounts freeze their `muted`
   * prop (see liveMutedProp), so the audible change travels via postMessage
   * here instead of a src rebuild.
   */
  const setLiveMuted = (muted: boolean) => {
    coordinator.setMuted(muted)
    findLiveController(containerRef.current)?.setMuted(muted)
  }

  // Unbridged audio can't change mute without an iframe reload (visible
  // flash + restart) — the chrome suppresses the toggle while it's active.
  const muteSuppressed =
    phase !== "ambient" &&
    currentItem?.kind === "audio" &&
    !!currentItem &&
    !hasMutableAudio(currentItem)

  /** The threshold tap — THE user gesture that lets audio in (§5). */
  const openThreshold = () => {
    if (!currentItem || phase !== "ambient") return
    coordinator.setActive(currentItem.id) // no-op when the loop-claim effect owns it
    if (currentItem.kind === "video" || currentItem.kind === "audio") {
      // A mounted live controller is the honest proxy for "the loop is
      // running": only the selected slide mounts live, and loop-eligible
      // implies bridged. A SPENT loop has already unmounted its iframe —
      // it falls through to the standard bridged path below.
      const loopController = currentLoops
        ? findLiveController(containerRef.current)
        : null
      if (loopController) {
        // The loop is already playing muted behind the die-cut — unmute IN
        // PLACE on the gesture (bridge ramp). Same iframe, no remount: the
        // loop never restarts at the threshold.
        setLiveMuted(false)
      } else if (isBridged(currentItem)) {
        // Bridged fresh mount: mount muted (autoplay-safe), unmute once the
        // record lands on the stage — in place via the bridge, no reload
        // (mediaLanded).
        unmuteOnLandRef.current = true
      } else {
        // Unbridged: the fresh mount carries the unmuted src — the
        // sanctioned cut; the provider's own play button is the fallback.
        coordinator.setMuted(false)
      }
    }
    setPhase("opening")
    // The tapped jacket face unmounts with the jacket — keep keyboard
    // control alive by handing focus straight back to the container.
    containerRef.current?.focus({ preventScroll: true })
  }

  /** Called by the shell when the jacket's departure animation completes.
   * (It also fires on the dock's exit at close start — the "opening" guard
   * makes that a no-op.) Sound no longer arrives here; see mediaLanded. */
  const jacketDeparted = () => {
    setPhase((prev) => (prev === "opening" ? "active" : prev))
  }

  /** The record landed on the platter (travel onComplete / the stage's
   * enter settling) — the deferred bridged unmute fires now, in place. */
  const mediaLanded = () => {
    if (unmuteOnLandRef.current) {
      unmuteOnLandRef.current = false
      setLiveMuted(false)
    }
  }

  /** Escape's first rung: begin the re-jacket. Mute lands immediately (the
   * re-jacket is silent); the phase holds at "closing" so media mounts
   * survive the reverse travel, and the shell reports closeSettled when the
   * record is home (or the stage has exited). A loop item keeps its slot
   * and its mount — the disc keeps spinning, re-muted in place. */
  const returnToAmbient = () => {
    // Already mid-close (Beat 1 or Beat 2): an Escape here continues the
    // close rather than restarting Beat 1.
    if (phase === "closing" || phase === "seating") return
    unmuteOnLandRef.current = false
    coordinator.setMuted(true)
    findLiveController(containerRef.current)?.setMuted(true)
    if (!currentLoops) coordinator.setActive(null)
    setPhase("closing")
    containerRef.current?.focus({ preventScroll: true })
  }

  /** Beat 1 done — the empty sleeve has crossfaded in composed. Travel kinds
   * hand off to Beat 2 (the record slides back in); article/audio have no
   * record to insert, so they settle straight to ambient. Fired from
   * SleeveCard's crossfade-in completion; the "closing" guard makes a late
   * fire after an Embla-select reset a no-op. */
  const sleeveComposed = () => {
    setPhase((prev) =>
      prev !== "closing" ? prev : currentTravels ? "seating" : "ambient",
    )
  }

  /** The close choreography settled — the sleeve is whole and the record is
   * home. Called from motion completion callbacks only (sanctioned setState
   * spot). */
  const closeSettled = () => {
    setPhase((prev) =>
      prev === "closing" || prev === "seating" ? "ambient" : prev,
    )
  }

  /** Advance to the next plate (Up Next completion/tap, peek tap, riffle). */
  const advanceNow = () => {
    if (api?.canScrollNext()) {
      // Reduced motion: jump, don't glide (Embla's jump flag).
      api.scrollNext(reduce)
    } else {
      setFinished(true)
    }
  }

  /** Set state value of `finished`, when scrolling to then end */
  const reachedTheEnd = () => {
    if (api) {
      const viewing = ["ambient", "active", "upnext"].includes(phase)
      if (viewing && !api.canScrollNext()) setFinished(true)
    }
  }

  /**
   * Next wrapper: mid-stack it turns the page next; at the LAST item it will forward to outro
   */
  const handleNext = () => {
    if (api?.canScrollNext() && !finished) scrollNext()
    else reachedTheEnd()
  }

  /**
   * Prev wrapper: mid-stack it turns the page back; at the outro it turns back onto the LAST item
   */
  const handlePrev = () => {
    if (!api?.canScrollNext() && finished) {
      containerRef.current?.focus({ preventScroll: true })
      setFinished(false)
      unmuteOnLandRef.current = false
      coordinator.setMuted(true)
      findLiveController(containerRef.current)?.setMuted(true)
      setPhase("ambient")
      if (!currentLoops) coordinator.setActive(null)
    } else {
      scrollPrev()
    }
  }

  // The gesture listeners below subscribe only on [api, finished], but the
  // turn handlers close over more than that (phase, currentLoops, …). Hold the
  // latest ones in a ref the effect reads through, so a turn always runs the
  // current closure without re-subscribing the listeners every render.
  const turnRef = useRef({ handleNext, handlePrev, reachedTheEnd })
  useEffect(() => {
    turnRef.current = { handleNext, handlePrev, reachedTheEnd }
  })

  /**
   * Passive listenners for events (wheel scrolling, swipe gesture)
   * A forward will trigger at the LAST item to opens the outro.
   * A backward will trigger on the outro for returning back to the LAST item.
   */
  useEffect(() => {
    const el = containerRef.current
    if (!el || !api) return

    /* A backward to feed at the LAST item, it will trigger when the scroll wheel's position < 0px. */
    const outroAtTop = () => {
      const scroller = el.querySelector<HTMLElement>("[data-outro-scroll]")
      return !scroller || scroller.scrollTop <= 0
    }

    /* A backward to feed at the LAST item, it will trigger when the scroll wheel's position < 0px. */
    const closeSleeveStage = (event: PointerEvent | WheelEvent) => {
      const contentElementAttrs =
        "[data-reader-scroll], [data-embed-well], iframe"
      const target = event.target as HTMLElement | null
      return target?.closest(contentElementAttrs)
    }

    // One page-turn per wheel gesture: fire on the first qualifying tick, then
    // hold a lock until the wheel (and its trackpad momentum) idles for a beat,
    // so a single flick never skips several plates. The lock lives in refs
    // (above) so a re-subscribe mid-flick can't unlock it.
    const onWheel = (event: WheelEvent) => {
      // Preventting trigger scrolling on the SleeveStage component (AudioWell, ArticleReader)
      if (closeSleeveStage(event)) return // if (target?.closest("[data-reader-scroll], [data-embed-well], iframe"))
      if (Math.abs(event.deltaY) < END_WHEEL_PX) return
      // The outro owns the wheel while itf is up: scroll-down is its content's to
      // scroll, scroll-up only turns back once it has bottomed out at the top.
      if (finished && (event.deltaY > 0 || !outroAtTop())) return

      window.clearTimeout(wheelIdleRef.current)
      wheelIdleRef.current = window.setTimeout(() => {
        wheelLockedRef.current = false
      }, WHEEL_IDLE_MS)
      if (wheelLockedRef.current) return
      wheelLockedRef.current = true

      if (event.deltaY > 0) turnRef.current.handleNext()
      else turnRef.current.handlePrev()
    }

    // A swipe is pointerdown, pointerup with enough travel. Skip regions that own
    // their own scroll (reader/embed) so a flick there never leaks into a turn.
    let swipeFromY: number | null = null
    const onPointerDown = (event: PointerEvent) => {
      swipeFromY = !finished && closeSleeveStage(event) ? null : event.clientY
    }

    const onPointerUp = (event: PointerEvent) => {
      if (swipeFromY === null) return
      const travelledUp = swipeFromY - event.clientY // + = finger up = forward
      swipeFromY = null
      // Swipe DOWN past the threshold, outro at its top. Then back onto the last item.
      if (finished) {
        if (-travelledUp > END_SWIPE_PX && outroAtTop())
          turnRef.current.handlePrev()
      } else if (travelledUp > END_SWIPE_PX) {
        turnRef.current.reachedTheEnd()
      }
    }

    el.addEventListener("wheel", onWheel, { passive: true })
    el.addEventListener("pointerdown", onPointerDown, { passive: true })
    el.addEventListener("pointerup", onPointerUp, { passive: true })

    return () => {
      el.removeEventListener("wheel", onWheel)
      el.removeEventListener("pointerdown", onPointerDown)
      el.removeEventListener("pointerup", onPointerUp)
    }
  }, [api, finished])

  /* Clear idle itemer of wheel scroll at unmount component state */
  useEffect(() => {
    return () => {
      window.clearTimeout(wheelIdleRef.current)
    }
  }, [])

  /** Up Next's "stay": remain indefinitely — latch out further auto-turns. */
  const stay = () => {
    if (currentItem) stayedRef.current.add(currentItem.id)
    setPhase("active")
  }

  const leave = () => {
    router.visit(`/playlists/${playlist.slug || playlist.id}`)
  }

  /** Escape ladder (§7): open → ambient; ambient → out. X always leaves. */
  const handleEscape = () => {
    if (phase === "ambient") leave()
    else returnToAmbient()
  }

  const replay = () => {
    api?.scrollTo(0, reduce)
    setFinished(false)
  }

  usePlayerKeyboard(containerRef, {
    onPrev: handlePrev,
    onNext: handleNext,
    onToggleMute: muteSuppressed ? undefined : () => setLiveMuted(!isMuted),
    onClose: handleEscape,
    onActivate: phase === "ambient" ? openThreshold : undefined,
  })

  return {
    containerRef,
    api,
    selectedIndex,
    scrollPrev,
    scrollNext,
    handlePrev,
    handleNext,
    phase,
    isOpen: phase !== "ambient",
    finished,
    current,
    next,
    isMuted,
    muteSuppressed,
    reduce,
    openThreshold,
    jacketDeparted,
    mediaLanded,
    returnToAmbient,
    sleeveComposed,
    closeSettled,
    advanceNow,
    stay,
    replay,
    handleEscape,
    leave,
    setLiveMuted,
  }
}
